import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { apiRequest } from "@/lib/queryClient";
import { CheckCircle2, HelpCircle, Loader2, Plus, X } from "lucide-react";

/**
 * "Request an account" — the in-app replacement for the sign-in card's mailto
 * footer (BRIEF-account-request-flow). Posts to the pre-auth
 * POST /org/:slug/api/account-requests endpoint, which creates pending account
 * row(s) in the vendor's Accounts list. The success state stays in-app.
 *
 * "+ Add another company" is for buyers who order for several locations or
 * companies under one phone number; the server maps that onto an account group
 * (one login, a company picker after PIN).
 */

interface AccountRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  vendorName: string;
  /** Pre-fill from whatever the buyer already typed into the sign-in field. */
  initialPhone?: string;
}

const MAX_COMPANIES = 10;

function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  return `${digits.slice(0, 1)}-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
}

export function AccountRequestDialog({ open, onOpenChange, vendorName, initialPhone }: AccountRequestDialogProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [companies, setCompanies] = useState<string[]>([""]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Reset every time the sheet opens so a second request starts clean, but keep
  // the phone the buyer already typed on the card.
  useEffect(() => {
    if (!open) return;
    setName("");
    setPhone(initialPhone ? formatPhone(initialPhone) : "");
    setEmail("");
    setCompanies([""]);
    setError(null);
    setSuccessMessage(null);
    setSubmitting(false);
  }, [open, initialPhone]);

  const phoneDigits = phone.replace(/\D/g, "");
  const trimmedCompanies = companies.map(c => c.trim());
  const emailTrimmed = email.trim();
  const emailLooksValid = emailTrimmed === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  const canSubmit =
    name.trim().length >= 2 &&
    phoneDigits.length >= 10 &&
    trimmedCompanies[0].length > 0 &&
    emailLooksValid &&
    !submitting;

  const updateCompany = (index: number, value: string) => {
    setCompanies(prev => prev.map((c, i) => (i === index ? value : c)));
  };
  const addCompany = () => {
    if (companies.length >= MAX_COMPANIES) return;
    setCompanies(prev => [...prev, ""]);
  };
  const removeCompany = (index: number) => {
    setCompanies(prev => (prev.length === 1 ? [""] : prev.filter((_, i) => i !== index)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await apiRequest("POST", "/api/account-requests", {
        name: name.trim(),
        phone: phoneDigits,
        email: emailTrimmed,
        companies: trimmedCompanies.filter(Boolean),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setSuccessMessage(
          data?.message ||
            `Thanks — your request has been sent to ${vendorName}. You'll get a text when your account is ready.`,
        );
      } else {
        setError(data?.message || "We couldn't send your request. Please try again.");
      }
    } catch {
      setError("We couldn't send your request. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[100dvh] overflow-y-auto" data-testid="dialog-account-request">
        {successMessage ? (
          <div className="text-center py-4 space-y-4" data-testid="account-request-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <DialogHeader className="space-y-2">
              <DialogTitle className="text-center">Request sent</DialogTitle>
              <DialogDescription className="text-center">{successMessage}</DialogDescription>
            </DialogHeader>
            <Button
              type="button"
              className="w-full h-11 bg-[#2B5397] hover:bg-[#244a85] text-white"
              onClick={() => onOpenChange(false)}
              data-testid="button-account-request-done"
            >
              Done
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            <DialogHeader>
              <DialogTitle>Request an account with {vendorName}</DialogTitle>
              <DialogDescription>
                Tell {vendorName} who you are and they&apos;ll set you up. You&apos;ll get a text when your account is ready.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="account-request-name">Your name</Label>
                <Input
                  id="account-request-name"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  autoComplete="name"
                  maxLength={80}
                  className="h-11 bg-[rgb(243,244,246)]"
                  data-testid="input-request-name"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="account-request-phone">Phone number</Label>
                <Input
                  id="account-request-phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="555-555-5555"
                  value={phone}
                  onChange={e => setPhone(formatPhone(e.target.value))}
                  autoComplete="tel"
                  maxLength={14}
                  className="h-11 bg-[rgb(243,244,246)]"
                  data-testid="input-request-phone"
                />
                <p className="text-xs text-gray-500">This is the number you&apos;ll sign in with.</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="account-request-email">
                  Email <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Input
                  id="account-request-email"
                  type="email"
                  inputMode="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  autoComplete="email"
                  maxLength={120}
                  className="h-11 bg-[rgb(243,244,246)]"
                  data-testid="input-request-email"
                />
                {!emailLooksValid && <p className="text-xs text-red-600">Enter a valid email address.</p>}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Label htmlFor="account-request-company-0">Company name</Label>
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600"
                          aria-label="Why add more than one company?"
                          data-testid="button-company-help"
                        >
                          <HelpCircle className="h-4 w-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
                        Order for more than one location or business? Add each one and you&apos;ll pick
                        which you&apos;re ordering for after you sign in — all under this one phone number.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {companies.map((company, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      id={`account-request-company-${index}`}
                      value={company}
                      onChange={e => updateCompany(index, e.target.value)}
                      placeholder={index === 0 ? "e.g. Main Street Café" : "Another company or location"}
                      autoComplete="organization"
                      maxLength={80}
                      className="h-11 bg-[rgb(243,244,246)]"
                      data-testid={`input-request-company-${index}`}
                    />
                    {companies.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeCompany(index)}
                        className="shrink-0 rounded-md p-2 text-gray-400 hover:text-red-600 hover:bg-red-50"
                        aria-label="Remove this company"
                        data-testid={`button-remove-company-${index}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}

                {companies.length < MAX_COMPANIES && (
                  <button
                    type="button"
                    onClick={addCompany}
                    className="inline-flex items-center gap-1 text-sm font-medium text-[#2B5397] hover:underline"
                    data-testid="button-add-company"
                  >
                    <Plus className="h-4 w-4" />
                    Add another company
                  </button>
                )}
              </div>
            </div>

            {error && (
              <p className="text-sm text-red-600" role="alert" data-testid="text-request-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full h-12 text-base bg-[#2B5397] hover:bg-[#244a85] text-white"
              disabled={!canSubmit}
              data-testid="button-submit-account-request"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Sending…
                </>
              ) : (
                "Send request"
              )}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
